import { Injectable, inject } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap, timeout } from 'rxjs/operators';

import { AuthenticationService } from './../../services/authentication-service/authentication.service';
import { environment } from './../../../environments/environment';
import { Auth0Service } from 'src/app/services/auth0-service/auth0.service';
import { Capacitor } from '@capacitor/core';
import { RegistrationUserStatusEnum } from 'src/app/models/registration';

/** Pass untouched request through to the next request handler. */
@Injectable()
export class AuthorizationInterceptor implements HttpInterceptor {
  private authService = inject(AuthenticationService);
  private auth0Service = inject(Auth0Service);

  private authorizationTokenHeader = environment.AUTHORIZATION_TOKEN_HEADER;
  private idTokenHeader = environment.IDTOKEN_HEADER;
  private platformName: string;
  private versionNumber: string;

  constructor() {
    this.platformName = Capacitor.getPlatform();
    this.versionNumber = environment.VERSION;
  }

  includeIdToken(requestEndpointName: string): boolean {
    return (
      requestEndpointName !== 'application' &&
      requestEndpointName !== 'forgot-password' &&
      requestEndpointName !== 'registration/financial' &&
      requestEndpointName !== 'registration/business' &&
      requestEndpointName !== 'registration/personal' &&
      requestEndpointName !== 'registration/assistance' &&
      requestEndpointName !== 'registration/profile' &&
      requestEndpointName !== 'registration/verify-email' &&
      requestEndpointName !== 'rest/planningtool/v1/eMoneyLauncher' &&
      requestEndpointName?.indexOf('au/registration') == -1
    );
  }

  includeAuthToken(requestEndpointName: string): boolean {
    return (
      requestEndpointName !== 'application' &&
      requestEndpointName !== 'forgot-password' &&
      requestEndpointName !== 'registration/assistance' &&
      requestEndpointName !== 'registration/user' &&
      requestEndpointName !== 'rest/planningtool/v1/eMoneyLauncher' &&
      requestEndpointName !== 'auth0/user/guid' &&
      requestEndpointName?.indexOf('au/registration') == -1
    );
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authToken = this.authService.getAuthToken();
    const idToken = this.auth0Service.retrieveIdToken();

    let actionId: number;
    const requestEndpointName = req.url.split('/api/')[1];
    const aemFormsEndpoint = req.url.indexOf('services/forms') !== -1;

    try {
      // dynatrace create custom action entry
      // @ts-ignore
      actionId = dtrum.enterAction(requestEndpointName, 'load');
    } catch (e) {}

    // IF we switch to a BFF endpoint we can probably remove this, AEM does not like the request modifications the interceptor makes
    if (aemFormsEndpoint) {
      return next.handle(req);
    }

    // We need the browser / app to dynmically set the headers for file uploads
    // so it will automatically include the bearer token
    if (
      !req.headers.has('Content-Type') &&
      requestEndpointName !== 'profile/documents-upload/file-upload' &&
      requestEndpointName !== 'rest/planningtool/v1/eMoneyLauncher'
    ) {
      req = req.clone({
        headers: req.headers.set('Content-Type', 'application/json'),
      });
    }

    if (!req.headers.has('Client-Platform')) {
      req = req.clone({
        headers: req.headers.set('Client-Platform', this.platformName),
      });
    }

    if (!req.headers.has('X-Email')) {
      req = req.clone({
        headers: req.headers.set('X-Email', this.authService.emailHeaderValue || ''),
      });
    }

    if (!req.headers.has('Client-Version')) {
      req = req.clone({
        headers: req.headers.set('Client-Version', this.versionNumber),
      });
    }

    // Only include the authorizationTokenHeader if it's not one of our external endpoints
    if (
      this.includeAuthToken(requestEndpointName) &&
      !req.headers.has(this.authorizationTokenHeader) &&
      authToken !== null
    ) {
      req = req.clone({
        headers: req.headers.set(this.authorizationTokenHeader, authToken),
      });
    }

    // Remove the authorizationTokenHeader if it exists and you're trying to register or trigger forgot password
    // We don't want the header being used with these endpoints
    if (
      !this.includeAuthToken(requestEndpointName) &&
      req.headers.has(this.authorizationTokenHeader)
    ) {
      req = req.clone({
        headers: req.headers.delete(this.authorizationTokenHeader, authToken),
      });
    }
    // Remove the idTokenHeader if it exists and you're trying to register or trigger forgot password
    // We don't want the header being used with these endpoints
    if (!this.includeIdToken(requestEndpointName) && req.headers.has(this.idTokenHeader)) {
      req = req.clone({
        headers: req.headers.delete(this.idTokenHeader, idToken),
      });
    }
    // Add this ID token for every subsequent request if it should be there
    if (
      this.includeIdToken(requestEndpointName) &&
      !req.headers.has(this.idTokenHeader) &&
      idToken !== null
    ) {
      req = req.clone({
        headers: req.headers.set(this.idTokenHeader, idToken),
      });
    }

    return next.handle(req).pipe(
      timeout(environment.API_TIMEOUT),
      tap(
        (evt) => {
          try {
            // closing the dynatrace request action
            // @ts-ignore
            dtrum.leaveAction(actionId);
          } catch (e) {}

          if (evt instanceof HttpResponse) {
            // If the response returned an authToken, Save it
            if (
              evt.headers.has(this.authorizationTokenHeader) &&
              !(
                req.url.includes('registration/user') &&
                evt.body.userStatus === RegistrationUserStatusEnum.PERSON_WITH_EXISTING_PROFILE
              )
            ) {
              this.authService.setAuthToken(evt.headers.get(this.authorizationTokenHeader));
            }
          }
          return next.handle(req);
        },
        (error: any) => {
          if (error instanceof HttpErrorResponse) {
            if (error && error.status === 401) {
              // If the error response returned an authToken,
              // then it came from login!  Save it
              if (error.headers.has(this.authorizationTokenHeader)) {
                this.authService.setAuthToken(error.headers.get(this.authorizationTokenHeader));
              }

              return next.handle(req);
            }
          }
          return of(error);
        },
      ),
    );
  }
}
